import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/MoreVert';
import { Button, Divider, IconButton, Menu, ListSubheader } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useQueryClient } from 'react-query';

import {
  NewConsultation,
  NewCommunication,
  NewFolder,
  Edit,
  PinUnpin,
  Report,
  Destroy,
  Clone,
  PublishUnpublish,
  ViewInDashboard,
  ViewPost,
  UpdateStatus,
  Merge,
  ContactAuthor,
  Assign,
  AddToFolder,
} from './ListItems';
import DeletePostDialog from '../../Posts/DeletePostDialog';
import CloneDialog from '../../Posts/CloneDialog';
import PublishingPostDialog from '../../Posts/PublishingPostDialog';
import ReportPostDialog from '../../Posts/MenuActionsDialogs/ReportPostDialog';
import ConsultationsMenu from '../../Posts/MenuCreatePost/ConsultationsMenu';
import CommunicationsMenu from '../../Posts/MenuCreatePost/CommunicationsMenu';
import FoldersMenu from '../../Posts/MenuCreatePost/FoldersMenu';
import Dialog from '../Dialog';
import { PostStatuses } from '../../../Utils/Types';
import CommentAndStatusUpdate from '../../Posts/Shared/CommentAndStatusUpdate';
import AssignmentModal from '../../Posts/Shared/AssignmentModal';
import AddToFolderModal from '../../Posts/Shared/AddToFolder/AddToFolderModal';
import usePinUnpinMutation from '../../../Hooks/usePinUnpinMutation';
import useSpace from '../../../Hooks/useSpace';
import useCurrentUser from '../../../Hooks/useCurrentUser';

function MenuActions({
  assign = false,
  addToFolder,
  destroy = false,
  clone = false,
  edit = false,
  viewInDashboard = false,
  newConsultation = false,
  newCommunication = false,
  newFolder,
  pinUnpin = false,
  post,
  publishUnPublish = false,
  contactAuthor,
  merge = false,
  report = false,
  commentId,
  isTextButton = false,
  viewPost = false,
  updateStatus,
  size = 'medium',
  discussionType,
  onDestroy = () => {},
}: any) {
  const intl = useIntl();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [modalOpen, setModalOpen] = useState({
    addToFolder: false,
    report: false,
    clone: false,
    delete: false,
    publish: false,
    newConsultation: false,
    newCommunication: false,
    newFolder: false,
    updateStatus: false,
    assign: false,
  });

  const { isLoggedIn } = useCurrentUser();
  const { space } = useSpace();

  const refreshDashboardPostList = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard-posts'] });
  };

  const {
    is_pinned: isPinned,
    id: postId,
    slug: postSlug,
    published_at: publishedAt,
    ask_to_publish_at: askToPublishAt,
  } = post;
  const userRole = space.current_user_role;

  const { mutate: pinUnpinPost, isLoading: pinUnpinIsFetching } = usePinUnpinMutation({
    post,
    onSuccess: refreshDashboardPostList,
  });

  const openMenu = (event: any) => setAnchorEl(event.currentTarget);

  const closeMenu = () => setAnchorEl(null);

  const handlePinUnpin = () => {
    pinUnpinPost(!isPinned, {
      onSuccess: closeMenu,
    });
  };

  const handleSetModalOpen = (modal: any, state: any) => {
    setModalOpen({ ...modalOpen, [modal]: state });
    if (state === false) {
      refreshDashboardPostList();
    }
    closeMenu();
  };

  const firstList = newConsultation || newCommunication || newFolder;
  const secondList =
    viewInDashboard ||
    viewPost ||
    edit ||
    addToFolder ||
    pinUnpin ||
    updateStatus ||
    contactAuthor ||
    merge;
  const thirdList = destroy || publishUnPublish || report || clone;
  const showSectionsSeparators = firstList && secondList;

  if (!firstList && !secondList && !thirdList) {
    return null;
  }

  if (!isLoggedIn) {
    return null;
  }

  const ButtonEl = isTextButton ? Button : IconButton;
  const buttonChildren = isTextButton ? (
    <FormattedMessage id="POSTS.CREATE_MENU.ACTIONS" />
  ) : (
    <MenuIcon />
  );

  return (
    <>
      <ButtonEl
        {...(isTextButton ? { endIcon: <ArrowDropDownIcon /> } : {})}
        color="primary"
        aria-controls="menu-actions"
        aria-haspopup="true"
        onClick={openMenu}
        aria-label="menu actions"
        size={size}
      >
        {buttonChildren}
      </ButtonEl>
      <Menu
        id="menu-actions"
        role="list"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={closeMenu}
        MenuListProps={{
          component: 'nav',
          disablePadding: true,
          subheader: showSectionsSeparators && (
            <ListSubheader disableSticky component="div">
              <FormattedMessage id="POSTS.CREATE_MENU.BUTTON" />
            </ListSubheader>
          ),
        }}
      >
        {newFolder && <NewFolder handleClick={() => handleSetModalOpen('newFolder', true)} />}
        {newConsultation && (
          <NewConsultation handleClick={() => handleSetModalOpen('newConsultation', true)} />
        )}
        {newCommunication && (
          <NewCommunication handleClick={() => handleSetModalOpen('newCommunication', true)} />
        )}

        {secondList && <Divider />}

        {viewInDashboard && !pathname.match('dashboard') && (
          <ViewInDashboard postSlug={postSlug} spaceSlug={space.slug} postType={post.type} />
        )}

        {viewPost && <ViewPost postSlug={postSlug} spaceSlug={space.slug} />}

        {edit && <Edit postSlug={postSlug} spaceSlug={space.slug} postType={post.type} />}

        {pinUnpin && (
          <PinUnpin
            isPinned={isPinned}
            handleClick={handlePinUnpin}
            isFetching={pinUnpinIsFetching}
          />
        )}
        {addToFolder && <AddToFolder handleClick={() => handleSetModalOpen('addToFolder', true)} />}

        {clone && <Clone handleClick={() => handleSetModalOpen('clone', true)} />}

        {updateStatus && (
          <UpdateStatus handleClick={() => handleSetModalOpen('updateStatus', post.status)} />
        )}

        {assign && <Assign handleClick={() => handleSetModalOpen('assign', true)} />}

        {merge && (
          <Merge handleClick={() => handleSetModalOpen('updateStatus', PostStatuses.DUPLICATE)} />
        )}

        {contactAuthor && (
          <ContactAuthor
            postSlug={postSlug}
            spaceSlug={space.slug}
            postType={post.type}
            onClick={closeMenu}
          />
        )}

        {thirdList && <Divider />}

        {publishUnPublish && (
          <PublishUnpublish
            isPublished={!!publishedAt}
            handleClick={() => handleSetModalOpen('publish', true)}
          />
        )}

        {report && <Report space={space} handleClick={() => handleSetModalOpen('report', true)} />}

        {destroy && <Destroy handleClick={() => handleSetModalOpen('delete', true)} />}
      </Menu>
      <AddToFolderModal
        open={modalOpen.addToFolder}
        onClose={() => handleSetModalOpen('addToFolder', false)}
        title={intl.formatMessage({ id: 'POST_FORM.CHOOSE_A_FOLDER' })}
        post={post}
      />
      {assign && (
        <AssignmentModal
          open={modalOpen.assign}
          onClose={() => handleSetModalOpen('assign', false)}
          post={post}
        />
      )}
      {updateStatus && modalOpen.updateStatus && (
        <CommentAndStatusUpdate
          open={Boolean(modalOpen.updateStatus)}
          posts={[post]}
          preSelectedStatus={modalOpen.updateStatus}
          onClose={() => handleSetModalOpen('updateStatus', false)}
          onSubmit={() => handleSetModalOpen('updateStatus', false)}
        />
      )}
      <Dialog
        id="dialog-new-consultation"
        label="dialog-new-consultation"
        open={modalOpen.newConsultation}
        onClose={() => handleSetModalOpen('newConsultation', false)}
        title={intl.formatMessage({ id: 'POSTS.CREATE_MENU.DIALOG_TITLE.CONSULTATION' })}
      >
        <ConsultationsMenu space={space} userRole={userRole} parentId={postId} />
      </Dialog>
      <Dialog
        id="dialog-new-communication"
        label="dialog-new-communication"
        open={modalOpen.newCommunication}
        onClose={() => handleSetModalOpen('newCommunication', false)}
        title={intl.formatMessage({ id: 'POSTS.CREATE_MENU.DIALOG_TITLE.COMMUNICATION' })}
      >
        <CommunicationsMenu space={space} userRole={userRole} parentId={postId} />
      </Dialog>
      <Dialog
        id="dialog-new-folder"
        label="dialog-new-folder"
        open={modalOpen.newFolder}
        onClose={() => handleSetModalOpen('newFolder', false)}
        title={intl.formatMessage({ id: 'POSTS.CREATE_MENU.BUTTON' })}
      >
        <FoldersMenu space={space} userRole={userRole} parentId={postId} />
      </Dialog>

      <DeletePostDialog
        postId={postId}
        commentId={commentId}
        open={modalOpen.delete}
        onClose={() => {
          handleSetModalOpen('delete', false);
          onDestroy();
        }}
        discussionType={discussionType}
      />
      <CloneDialog
        postId={postId}
        open={modalOpen.clone}
        onClose={() => {
          handleSetModalOpen('clone', false);
        }}
      />
      <PublishingPostDialog
        action={!publishedAt || askToPublishAt ? 'publish' : 'unpublish'}
        postId={postId}
        publishedAt={publishedAt}
        open={modalOpen.publish}
        onClose={() => handleSetModalOpen('publish', false)}
        post={post}
      />
      {report && modalOpen.report && (
        <ReportPostDialog
          post={post}
          commentId={commentId}
          open={modalOpen.report}
          onClose={() => handleSetModalOpen('report', false)}
          space={space}
        />
      )}
    </>
  );
}

MenuActions.propTypes = {
  assign: PropTypes.bool,
  destroy: PropTypes.bool,
  clone: PropTypes.bool,
  edit: PropTypes.bool,
  newConsultation: PropTypes.bool,
  newCommunication: PropTypes.bool,
  pinUnpin: PropTypes.bool,
  viewInDashboard: PropTypes.bool,
  viewPost: PropTypes.bool,
  post: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string.isRequired,
    is_pinned: PropTypes.bool.isRequired,
    type: PropTypes.string.isRequired,
    publishedAt: PropTypes.string,
  }).isRequired,
  publishUnPublish: PropTypes.bool,
  report: PropTypes.bool,
  merge: PropTypes.bool,
  isTextButton: PropTypes.bool,
  discussionType: PropTypes.string,
  commentId: PropTypes.number,
  onDestroy: PropTypes.func,
};

export default MenuActions;
